'use client'

import { useCurrentUser } from '@/hooks/use-current-user'
import { checkAndEnsureUserProfile } from '@/lib/auth/middleware/profile-check'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

interface ProfileCheckStatus {
  isChecking: boolean
  isComplete: boolean
  needsRedirect: boolean
  error: string | null
}

/**
 * Hook to check if the user's profile is properly set up after authentication
 * This is especially important for phone number authentication
 */
export function useProfileCheck() {
  const user = useCurrentUser()
  const router = useRouter()
  const [status, setStatus] = useState<ProfileCheckStatus>({
    isChecking: false,
    isComplete: true,
    needsRedirect: false,
    error: null
  })

  useEffect(() => {
    // Only run check if user is authenticated
    if (!user?.id) return

    let isMounted = true
    const validateProfile = async () => {
      // Avoid duplicate checks
      if (status.isChecking) return

      try {
        setStatus(prev => ({ ...prev, isChecking: true }))

        // Call the server action to check and ensure profile
        const result = await checkAndEnsureUserProfile(user.id)

        if (!isMounted) return

        // Update status based on result
        setStatus({
          isChecking: false,
          isComplete: result.isComplete,
          needsRedirect: !!result.redirectUrl,
          error: null
        })

        // Handle redirects if necessary
        if (result.redirectUrl) {
          router.push(result.redirectUrl)
        }
      } catch (error) {
        if (!isMounted) return

        setStatus({
          isChecking: false,
          isComplete: false,
          needsRedirect: false,
          error: error instanceof Error ? error.message : 'Failed to verify profile'
        })
      }
    }

    validateProfile()

    return () => {
      isMounted = false
    }
  }, [user?.id, router])

  return status
}
